import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";
import { Loader2, Check, X, Shield, Image as ImageIcon, Users, Search, ExternalLink, BadgeCheck, Trash2, Flag, BarChart3, Sparkles, Send, Coins, Download } from "lucide-react";
import { useAuth } from "@/lib/auth";

const StatCard = ({ label, value, accent, testId }) => (
  <div
    className={`rounded-xl border p-3 ${accent ? "bg-[#722f37]/5 border-[#722f37]/30" : "bg-[#faf9f6] border-[#e5e0d8]"}`}
    data-testid={testId}
  >
    <div className="text-[10px] uppercase tracking-wider text-[#5c5c5c]">{label}</div>
    <div className={`mt-1 font-serif-display text-2xl ${accent ? "text-[#722f37]" : "text-[#1a1a1a]"}`}>{value}</div>
  </div>
);

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("stats");
  const [queue, setQueue] = useState({ pending_photos: [], pending_verifications: [] });
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [q, u, r, s] = await Promise.all([
        api.get("/admin/queue"),
        api.get("/admin/users"),
        api.get("/admin/reports").catch(() => ({ data: { reports: [] } })),
        api.get("/admin/stats").catch(() => ({ data: null })),
      ]);
      setQueue(q.data);
      setUsers(u.data.users || []);
      setReports(r.data.reports || []);
      setStats(s.data);
    } catch (err) {
      toast.error(errorMessage(err, "Could not load admin data"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const moderatePhoto = async (photoId, status) => {
    setBusyId(photoId);
    try {
      await api.post(`/admin/photos/${photoId}/moderate`, { status });
      toast.success(`Photo ${status}`);
      setQueue((q) => ({ ...q, pending_photos: q.pending_photos.filter((p) => p.photo_id !== photoId) }));
    } catch (err) { toast.error(errorMessage(err, "Failed")); }
    finally { setBusyId(null); }
  };

  const moderateVerification = async (userId, status) => {
    setBusyId(userId);
    try {
      await api.post(`/admin/verifications/${userId}/moderate`, { status });
      toast.success(`Verification ${status}`);
      setQueue((q) => ({ ...q, pending_verifications: q.pending_verifications.filter((v) => v.user_id !== userId) }));
    } catch (err) { toast.error(errorMessage(err, "Failed")); }
    finally { setBusyId(null); }
  };

  const toggleVerified = async (userId) => {
    setBusyId(userId);
    try {
      const { data } = await api.post(`/admin/users/${userId}/toggle-verified`);
      setUsers((us) => us.map((u) => (u.id === userId ? { ...u, verified: data.verified } : u)));
      toast.success(`Verified → ${data.verified ? "ON" : "OFF"}`);
    } catch (err) { toast.error(errorMessage(err, "Failed")); }
    finally { setBusyId(null); }
  };

  const removeUser = async (u) => {
    const first = window.confirm(`Remove ${u.display_name || u.email}? This deletes their profile, photos, ratings, matches and messages. This cannot be undone.`);
    if (!first) return;
    const typed = window.prompt(`To confirm, type the member's email exactly:\n${u.email}`);
    if (typed !== u.email) {
      if (typed !== null) toast.error("Email didn't match — nothing was deleted.");
      return;
    }
    setBusyId(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((us) => us.filter((x) => x.id !== u.id));
      toast.success(`${u.display_name || u.email} removed`);
    } catch (err) {
      toast.error(errorMessage(err, "Delete failed"));
    } finally {
      setBusyId(null);
    }
  };

  const resolveReport = async (rid) => {
    setBusyId(rid);
    try {
      await api.post(`/admin/reports/${rid}/resolve`);
      setReports((rs) => rs.filter((r) => r.id !== rid));
      toast.success("Report resolved");
    } catch (err) { toast.error(errorMessage(err, "Failed")); }
    finally { setBusyId(null); }
  };

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.display_name || "").toLowerCase().includes(q)
      || (u.email || "").toLowerCase().includes(q)
      || (u.location || "").toLowerCase().includes(q)
    );
  }, [query, users]);

  const exportUsersCsv = () => {
    const rows = filteredUsers;
    if (rows.length === 0) {
      toast.error("No members to export.");
      return;
    }
    const headers = [
      "id", "email", "display_name", "age", "gender", "seeking", "location",
      "verified", "verification_status", "is_admin",
      "subscription_active", "subscription_plan", "reveal_credits",
      "successful_referrals", "lifetime_ratings_given", "streak_days",
      "photos_total", "photos_approved", "photos_pending",
      "overall_rating", "rating_count", "golden", "voice_intro", "created_at",
    ];
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const lines = [headers.join(",")];
    for (const u of rows) {
      const golden = (u.overall_rating >= 8 && u.rating_count > 0) ? "yes" : "no";
      lines.push([
        u.id, u.email, u.display_name, u.age ?? "", u.gender, u.seeking, u.location,
        u.verified ? "yes" : "no", u.verification_status, u.is_admin ? "yes" : "no",
        u.subscription_active ? "yes" : "no", u.subscription_plan || "",
        u.reveal_credits, u.successful_referrals, u.lifetime_ratings_given, u.streak_days,
        u.photo_counts?.total ?? 0, u.photo_counts?.approved ?? 0, u.photo_counts?.pending ?? 0,
        u.overall_rating, u.rating_count, golden,
        u.voice_intro ? "yes" : "no", u.created_at || "",
      ].map(escape).join(","));
    }
    const csv = "\uFEFF" + lines.join("\r\n"); // BOM so Excel opens UTF-8 correctly
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `primeconnect-members-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} member${rows.length === 1 ? "" : "s"} to CSV`);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#722f37]" />
      </div>
    );
  }

  if (user && !user.is_admin) return <Navigate to="/discover" replace />;

  const TabBtn = ({ id, icon: Icon, label, count }) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition-colors ${
        tab === id ? "bg-[#722f37] text-white" : "bg-white text-[#722f37] border border-[#e5e0d8] hover:bg-[#faf9f6]"
      }`}
      data-testid={`admin-tab-${id}`}
    >
      <Icon className="w-4 h-4" /> {label}
      {typeof count === "number" && (
        <span className={`ml-1 rounded-full px-2 py-0.5 text-[10px] ${tab === id ? "bg-white/25 text-white" : "bg-[#eae5d9] text-[#5c5c5c]"}`}>
          {count}
        </span>
      )}
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="admin-root">
      <span className="text-sm uppercase tracking-[0.28em] font-bold text-[#722f37]">Manager</span>
      <h1 className="font-serif-display text-4xl md:text-5xl mt-2">Admin panel.</h1>
      <p className="mt-3 text-lg text-[#5c5c5c]">Manage members, moderate photos and approve verifications.</p>

      <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="admin-tabs">
        <TabBtn id="stats" icon={BarChart3} label="Stats" />
        <TabBtn id="users" icon={Users} label="Users" count={users.length} />
        <TabBtn id="reports" icon={Flag} label="Reports" count={reports.length} />
        <TabBtn id="photos" icon={ImageIcon} label="Pending photos" count={queue.pending_photos.length} />
        <TabBtn id="verifications" icon={Shield} label="Verifications" count={queue.pending_verifications.length} />
      </div>

      {tab === "stats" && stats && (
        <section className="mt-8 space-y-6" data-testid="admin-stats-section">
          {/* Signups */}
          <div className="rounded-2xl bg-white border border-[#e5e0d8] card-elev p-6">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-bold text-[#722f37]">
              <Users className="w-4 h-4" /> Member signups
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Total members" value={stats.signups.total} testId="stat-total-signups" />
              <StatCard label="Last 24h" value={stats.signups.new_24h} accent />
              <StatCard label="Last 7 days" value={stats.signups.new_7d} accent />
              <StatCard label="Last 30 days" value={stats.signups.new_30d} accent />
              <StatCard label="Verified" value={stats.signups.verified} />
              <StatCard label="Active subscribers" value={stats.signups.subscribed} />
              <StatCard label="With photos" value={stats.signups.with_photos} />
              <StatCard label="With voice intro" value={stats.signups.with_voice_intro} />
            </div>
            {stats.signups.daily?.length > 0 && (
              <div className="mt-6">
                <div className="text-xs text-[#5c5c5c] mb-2">Daily signups (last 14 days)</div>
                <div className="flex items-end gap-1 h-24">
                  {stats.signups.daily.map((d) => {
                    const max = Math.max(...stats.signups.daily.map((x) => x.count), 1);
                    const h = Math.max(4, Math.round((d.count / max) * 90));
                    return (
                      <div key={d.day} className="flex-1 flex flex-col items-center gap-1" title={`${d.day}: ${d.count}`}>
                        <div className="w-full rounded-t bg-[#722f37]" style={{ height: `${h}px` }} />
                        <span className="text-[9px] text-[#5c5c5c]">{d.day.slice(5)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Ratings + Golden */}
          <div className="rounded-2xl bg-white border border-[#e5e0d8] card-elev p-6">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-bold text-[#722f37]">
              <Sparkles className="w-4 h-4" /> Ratings
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Ratings total" value={stats.ratings.total} />
              <StatCard label="From members" value={stats.ratings.human} />
              <StatCard label="From Gemini AI" value={stats.ratings.ai_gemini} />
              <StatCard label="Last 7 days" value={stats.ratings.new_7d} accent />
              <StatCard label="Average score" value={`${stats.ratings.avg_score}/10`} />
              <StatCard label="8+ ratings" value={stats.ratings.high_score_count} />
              <StatCard label="Golden members" value={stats.ratings.golden_users} accent />
            </div>
          </div>

          {/* Matches + Revenue */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl bg-white border border-[#e5e0d8] card-elev p-6">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-bold text-[#722f37]">
                <Send className="w-4 h-4" /> Matches & messaging
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <StatCard label="Matches" value={stats.matches.matches} />
                <StatCard label="Paid unlocks" value={stats.matches.paid_unlocks} accent />
                <StatCard label="Messages" value={stats.matches.messages_total} />
                <StatCard label="Messages 7d" value={stats.matches.messages_7d} />
              </div>
            </div>
            <div className="rounded-2xl bg-white border border-[#e5e0d8] card-elev p-6">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-bold text-[#722f37]">
                <Coins className="w-4 h-4" /> Revenue (Stripe)
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <StatCard label="All-time" value={`$${stats.revenue.all_time_usd.toLocaleString()}`} accent />
                <StatCard label="Transactions" value={stats.revenue.transactions} />
                <StatCard label="Last 7 days" value={`$${stats.revenue.revenue_7d_usd.toLocaleString()}`} />
                <StatCard label="Last 30 days" value={`$${stats.revenue.revenue_30d_usd.toLocaleString()}`} />
              </div>
            </div>
          </div>

          {/* Moderation */}
          <div className="rounded-2xl bg-white border border-[#e5e0d8] card-elev p-6">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-bold text-[#722f37]">
              <Shield className="w-4 h-4" /> Moderation queue
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Open reports" value={stats.moderation.open_reports} accent={stats.moderation.open_reports > 0} />
              <StatCard label="Resolved reports" value={stats.moderation.resolved_reports} />
              <StatCard label="Pending photos" value={stats.moderation.pending_photos} accent={stats.moderation.pending_photos > 0} />
              <StatCard label="Pending verifications" value={stats.moderation.pending_verifications} accent={stats.moderation.pending_verifications > 0} />
            </div>
          </div>
        </section>
      )}

      {tab === "users" && (
        <section className="mt-8" data-testid="admin-users-section">
          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5c5c5c]" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, email or location"
                className="w-full pl-9 pr-3 py-2 rounded-full bg-white border border-[#e5e0d8] text-sm focus:outline-none focus:border-[#722f37]"
                data-testid="admin-users-search"
              />
            </div>
            <span className="text-xs text-[#5c5c5c]" data-testid="admin-users-count">
              {filteredUsers.length} of {users.length}
            </span>
            <button
              type="button"
              onClick={exportUsersCsv}
              disabled={filteredUsers.length === 0}
              className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#722f37] text-white px-4 py-2 text-xs font-semibold hover:bg-[#5e2229] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="admin-users-download-csv"
              title="Download the currently visible members as a CSV file"
            >
              <Download className="w-4 h-4" />
              Download members.csv
            </button>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-[#e5e0d8] bg-white card-elev">
            <table className="w-full text-sm">
              <thead className="bg-[#faf9f6] text-[#5c5c5c] uppercase text-[10px] tracking-[0.2em]">
                <tr>
                  <th className="text-left px-4 py-3">Member</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Location</th>
                  <th className="text-left px-4 py-3">Rating</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">Plan</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">Credits</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Photos</th>
                  <th className="text-left px-4 py-3">Verified</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="border-t border-[#e5e0d8] hover:bg-[#faf9f6]" data-testid={`admin-user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <span className="font-semibold text-[#1a1a1a]">{u.display_name || "(no name)"}</span>
                        {u.age && <span className="text-[#5c5c5c] text-xs">· {u.age}</span>}
                        {u.is_admin && <span className="ml-2 rounded-full bg-[#722f37] text-white text-[10px] font-bold px-2 py-0.5">ADMIN</span>}
                      </div>
                      <div className="text-xs text-[#5c5c5c]">{u.email}</div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-xs text-[#5c5c5c]">{u.location || "—"}</td>
                    <td className="px-4 py-3">
                      {u.rating_count > 0 ? (
                        <span className="text-xs">
                          <span className={`font-bold ${u.overall_rating >= 8 ? "text-[#c39d2b]" : "text-[#1a1a1a]"}`}>
                            {u.overall_rating.toFixed(1)}
                          </span>
                          <span className="text-[#5c5c5c]"> ({u.rating_count})</span>
                        </span>
                      ) : (
                        <span className="text-xs text-[#5c5c5c]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {u.subscription_active ? (
                        <span className="text-xs text-[#8a6a10] font-semibold">{u.subscription_plan || "active"}</span>
                      ) : (
                        <span className="text-xs text-[#5c5c5c]">free</span>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-xs">{u.reveal_credits}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-xs">
                      <span className="text-[#1a1a1a]">{u.photo_counts.approved}</span>
                      {u.photo_counts.pending > 0 && (
                        <span className="text-[#a02b2b] ml-1">(+{u.photo_counts.pending} pending)</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {u.verified ? (
                        <span className="inline-flex items-center gap-1 text-xs text-[#722f37]" data-testid={`admin-user-verified-${u.id}`}>
                          <BadgeCheck className="w-4 h-4" /> Yes
                        </span>
                      ) : (
                        <span className="text-xs text-[#5c5c5c]">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <Link
                          to={`/u/${u.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-[#722f37] hover:underline"
                          data-testid={`admin-user-view-${u.id}`}
                        >
                          View <ExternalLink className="w-3 h-3" />
                        </Link>
                        <button
                          type="button"
                          onClick={() => toggleVerified(u.id)}
                          disabled={busyId === u.id}
                          className="text-xs rounded-full border border-[#e5e0d8] px-2.5 py-1 hover:bg-[#faf9f6] disabled:opacity-50"
                          data-testid={`admin-user-toggle-verified-${u.id}`}
                          title="Toggle verified badge"
                        >
                          {busyId === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : (u.verified ? "Unverify" : "Verify")}
                        </button>
                        {!u.is_admin && u.id !== user?.id && (
                          <button
                            type="button"
                            onClick={() => removeUser(u)}
                            disabled={busyId === u.id}
                            className="text-xs rounded-full border border-[#a02b2b]/30 text-[#a02b2b] px-2.5 py-1 hover:bg-[#a02b2b]/5 disabled:opacity-50 inline-flex items-center gap-1"
                            data-testid={`admin-user-remove-${u.id}`}
                            title="Delete this member and all their data"
                          >
                            <Trash2 className="w-3 h-3" /> Remove
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-[#5c5c5c]">No members match your search.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === "reports" && (
        <section className="mt-8" data-testid="admin-reports-section">
          {reports.length === 0 ? (
            <div className="p-8 rounded-2xl bg-white border border-[#e5e0d8] text-[#5c5c5c]" data-testid="admin-reports-empty">
              No open reports. 👏
            </div>
          ) : (
            <div className="space-y-3" data-testid="admin-reports-list">
              {reports.map((r) => (
                <div key={r.id} className="rounded-2xl bg-white border border-[#e5e0d8] card-elev p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`admin-report-${r.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="rounded-full bg-[#a02b2b]/10 text-[#a02b2b] text-[10px] font-bold px-2 py-1 uppercase tracking-wider">
                        {r.reason.replace(/_/g, " ")}
                      </span>
                      <span className="text-xs text-[#5c5c5c]">{new Date(r.created_at).toLocaleString()}</span>
                    </div>
                    <div className="mt-2 text-sm">
                      <span className="text-[#5c5c5c]">Reporter:</span>{" "}
                      <span className="font-medium">{r.reporter_email}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-[#5c5c5c]">Target:</span>{" "}
                      <Link to={`/u/${r.target_id}`} className="font-medium text-[#722f37] hover:underline">
                        {r.target_display_name || r.target_email} <ExternalLink className="inline w-3 h-3" />
                      </Link>
                    </div>
                    {r.notes && (
                      <p className="mt-2 text-sm text-[#1a1a1a] whitespace-pre-line rounded-lg bg-[#faf9f6] border border-[#e5e0d8] p-3">
                        {r.notes}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => resolveReport(r.id)}
                      disabled={busyId === r.id}
                      className="btn-outline !py-2 !px-4 text-sm flex items-center gap-1.5"
                      data-testid={`admin-report-resolve-${r.id}`}
                    >
                      {busyId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      Resolve
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === "photos" && (
        <section className="mt-8" data-testid="admin-photos-section">
          {queue.pending_photos.length === 0 ? (
            <div className="p-8 rounded-2xl bg-white border border-[#e5e0d8] text-[#5c5c5c]" data-testid="admin-photos-empty">
              No pending photos.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-photos-grid">
              {queue.pending_photos.map((p) => (
                <div key={p.photo_id} className="rounded-2xl overflow-hidden bg-white border border-[#e5e0d8] card-elev" data-testid={`admin-photo-${p.photo_id}`}>
                  <img src={p.data_url} alt="" className="w-full aspect-[4/5] object-cover" />
                  <div className="p-4">
                    <div className="text-sm font-medium">{p.user_display_name}</div>
                    <div className="text-xs text-[#5c5c5c]">{p.user_email}</div>
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <button
                        onClick={() => moderatePhoto(p.photo_id, "rejected")}
                        disabled={busyId === p.photo_id}
                        className="btn-outline !py-2 flex items-center justify-center gap-1"
                        data-testid={`admin-photo-reject-${p.photo_id}`}
                      >
                        <X className="w-4 h-4" /> Reject
                      </button>
                      <button
                        onClick={() => moderatePhoto(p.photo_id, "approved")}
                        disabled={busyId === p.photo_id}
                        className="btn-primary !py-2 flex items-center justify-center gap-1"
                        data-testid={`admin-photo-approve-${p.photo_id}`}
                      >
                        <Check className="w-4 h-4" /> Approve
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === "verifications" && (
        <section className="mt-8" data-testid="admin-verifications-section">
          {queue.pending_verifications.length === 0 ? (
            <div className="p-8 rounded-2xl bg-white border border-[#e5e0d8] text-[#5c5c5c]" data-testid="admin-verif-empty">
              No pending verifications.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-verif-grid">
              {queue.pending_verifications.map((v) => (
                <div key={v.user_id} className="rounded-2xl overflow-hidden bg-white border border-[#e5e0d8] card-elev" data-testid={`admin-verif-${v.user_id}`}>
                  {v.selfie ? (
                    <img src={v.selfie} alt="Selfie" className="w-full aspect-[4/5] object-cover" />
                  ) : (
                    <div className="w-full aspect-[4/5] bg-[#eae5d9] flex items-center justify-center text-[#5c5c5c]">No selfie</div>
                  )}
                  <div className="p-4">
                    <div className="text-sm font-medium">{v.user_display_name}</div>
                    <div className="text-xs text-[#5c5c5c]">{v.user_email}</div>
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <button
                        onClick={() => moderateVerification(v.user_id, "rejected")}
                        disabled={busyId === v.user_id}
                        className="btn-outline !py-2 flex items-center justify-center gap-1"
                        data-testid={`admin-verif-reject-${v.user_id}`}
                      >
                        <X className="w-4 h-4" /> Reject
                      </button>
                      <button
                        onClick={() => moderateVerification(v.user_id, "approved")}
                        disabled={busyId === v.user_id}
                        className="btn-primary !py-2 flex items-center justify-center gap-1"
                        data-testid={`admin-verif-approve-${v.user_id}`}
                      >
                        <Check className="w-4 h-4" /> Approve
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
